import { apiCampaignSend, apiMerchantInitiateRedemption, apiValidateRedemption } from '@/services/api';

export const AI_ACTION_TYPES = {
  RUN_CAMPAIGN: 'RUN_CAMPAIGN',
  TARGET_CUSTOMERS: 'TARGET_CUSTOMERS',
  SEND_REMINDER: 'SEND_REMINDER',
  BOOST_VISITS: 'BOOST_VISITS',
  REWARD_USERS: 'REWARD_USERS',
  REDEEM: 'REDEEM',
};

const CAMPAIGN_BY_ACTION = {
  [AI_ACTION_TYPES.RUN_CAMPAIGN]: 'win_back',
  [AI_ACTION_TYPES.TARGET_CUSTOMERS]: 'visit_booster',
  [AI_ACTION_TYPES.SEND_REMINDER]: 'visit_booster',
  [AI_ACTION_TYPES.BOOST_VISITS]: 'visit_booster',
  [AI_ACTION_TYPES.REWARD_USERS]: 'high_frequency_reward',
};

const CAMPAIGN_META = {
  win_back: {
    campaign_type: 'behavioural',
    campaign_name: 'Come Back Offer',
    message: 'We miss you! Come back and unlock bonus loyalty rewards this week.',
  },
  visit_booster: {
    campaign_type: 'behavioural',
    campaign_name: 'Visit Again Offer',
    message: 'Visit this week and earn extra points on your next order.',
  },
  high_frequency_reward: {
    campaign_type: 'behavioural',
    campaign_name: 'VIP Thank You Offer',
    message: 'VIP update: you are eligible for premium loyalty rewards on your next visit.',
  },
  birthday_campaign: {
    campaign_type: 'occasion_based',
    campaign_name: 'Birthday Offer',
    message: 'Birthday rewards are ready for you. Visit soon to unlock your offer.',
  },
  customer_anniversary: {
    campaign_type: 'occasion_based',
    campaign_name: 'Anniversary Offer',
    message: 'Happy dining anniversary! Enjoy a special loyalty reward this week.',
  },
  restaurant_anniversary: {
    campaign_type: 'occasion_based',
    campaign_name: 'Restaurant Celebration',
    message: 'We are celebrating our anniversary! Visit now for special loyalty perks.',
  },
  festival_campaign: {
    campaign_type: 'occasion_based',
    campaign_name: 'Festival Offer',
    message: 'Festival rewards are live now. Join us and earn bonus loyalty points.',
  },
};

function normalizeSegment(segment) {
  return String(segment || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

function coercePositiveInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(Math.floor(n), 0);
}

function inferBestRedeemPoints(rawPoints) {
  const points = coercePositiveInt(rawPoints);
  const rewardTiers = [2500, 1000, 500, 300];
  for (const tier of rewardTiers) {
    if (points >= tier) return tier;
  }
  if (points >= 100) return 100;
  return 0;
}

function resolveCampaignSubtype(type, context = {}) {
  if (context?.campaign_subtype) return String(context.campaign_subtype).trim();

  const segment = normalizeSegment(context?.segment);
  if (segment === 'LOST' || segment === 'AT_RISK' || segment === 'RISK') return 'win_back';
  if (segment === 'ACTIVE' || segment === 'VIP') return 'high_frequency_reward';

  return CAMPAIGN_BY_ACTION[type] || 'visit_booster';
}

export function buildAIActionRequest(type, context = {}) {
  const safeType = AI_ACTION_TYPES[type] ? type : AI_ACTION_TYPES.RUN_CAMPAIGN;
  if (safeType === AI_ACTION_TYPES.REDEEM) {
    const customerId = String(context?.customerId || context?.customer_id || '').trim();
    const points = coercePositiveInt(context?.points || context?.pointsToRedeem || 0)
      || inferBestRedeemPoints(context?.customerPoints || context?.points_balance || 0);
    const applyBonus = Boolean(context?.apply_bonus_on_redemption);
    return {
      actionType: safeType,
      campaignSubtype: '',
      payload: {
        customer_id: customerId,
        points,
        apply_bonus_on_redemption: applyBonus,
      },
    };
  }

  const campaignSubtype = resolveCampaignSubtype(safeType, context);
  const meta = CAMPAIGN_META[campaignSubtype] || CAMPAIGN_META.visit_booster;

  return {
    actionType: safeType,
    campaignSubtype,
    payload: {
      campaign_type: context?.campaign_type || meta.campaign_type,
      campaign_subtype: campaignSubtype,
      campaign_name: context?.campaign_name || meta.campaign_name,
      message: context?.message || meta.message,
      send_now: context?.send_now !== false,
      scheduled_for: context?.scheduled_for || '',
    },
  };
}

function formatExecutionMessage(base, campaign = {}, fallbackAudience = 0) {
  const sent = Number(campaign?.sent_count || 0);
  const delivered = Number(campaign?.delivered_count || 0);
  const failed = Number(campaign?.failed_count || 0);
  const audience = Number(campaign?.audience_count || fallbackAudience || 0);
  const campaignName = String(campaign?.campaign_name || campaign?.campaign_label || '').trim();
  const defaultLead = campaignName
    ? `Campaign "${campaignName}" sent to ${audience} customers.`
    : `Campaign sent to ${audience} customers.`;
  const lead = String(base || '').trim() || defaultLead;
  const leadWithName = campaignName && !lead.toLowerCase().includes(campaignName.toLowerCase())
    ? `${lead} (${campaignName})`
    : lead;
  if (!sent && !delivered && !failed) return leadWithName;
  return `${leadWithName} Delivered: ${delivered} | Failed: ${failed}`;
}

export async function executeAIAction(type, context = {}) {
  const built = buildAIActionRequest(type, context);
  if (built.actionType === AI_ACTION_TYPES.REDEEM) {
    if (!built.payload.customer_id) {
      throw new Error('Customer is required for redemption');
    }
    if (!built.payload.points || built.payload.points < 100) {
      throw new Error('No reward or points available');
    }
    const initiated = await apiMerchantInitiateRedemption(
      built.payload.customer_id,
      built.payload.points,
      built.payload.apply_bonus_on_redemption
    );
    const validated = await apiValidateRedemption('3457', initiated?.redemption_id || null);
    const spent = coercePositiveInt(validated?.points_deducted || built.payload.points);
    const value = Number((spent / 100).toFixed(2));
    const customerName = String(validated?.customer_name || context?.customerName || 'Customer');
    return {
      success: true,
      actionType: built.actionType,
      campaignSubtype: '',
      campaign: null,
      raw: { initiated, validated },
      message: `$${value.toFixed(2)} redeemed for ${customerName}. Points used: ${spent}. Remaining: ${coercePositiveInt(validated?.new_balance)}`,
    };
  }

  const res = await apiCampaignSend(built.payload);
  const campaign = res?.campaign || {};
  return {
    success: true,
    actionType: built.actionType,
    campaignSubtype: built.campaignSubtype,
    campaign,
    raw: res,
    message: formatExecutionMessage(res?.message, campaign, context?.audience_count),
  };
}

export function pickPrimaryInsight(candidates = []) {
  const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
  if (!list.length) return null;

  const typeWeight = { risk: 90, opportunity: 70, neutral: 45 };
  return [...list]
    .map((item, index) => {
      const explicit = Number(item?.priority_score || 0);
      const valueMagnitude = Math.abs(Number(item?.value || 0));
      const weight = typeWeight[String(item?.type || 'neutral')] || typeWeight.neutral;
      return { ...item, __rank: explicit + weight + valueMagnitude, __index: index };
    })
    .sort((a, b) => (b.__rank - a.__rank) || (a.__index - b.__index))[0];
}
